import { useState } from 'react';
import { Button, TooltipProvider } from '@design-system';
import { fundingPolicyTypeDefs, hasKnownIncorrectModeling } from '@shared';
import { UnverifiedModelingDisclaimer } from '@client/components/UnverifiedModelingDisclaimer';
import { useProjectionForPolicy, usePolicyById } from '../hooks/useProjectionForPolicy';
import { useIsWizardSession } from '../hooks/useIsWizardSession';
import { PolicyDetailsPopover } from './PolicyDetailsPopover';
import { CarePlanDetailsPopover } from './carePlanDetails/CarePlanDetailsPopover';
import { ReturnsSummaryCard } from './ReturnsSummaryCard';
import { MonthlyBreakdownTable } from './MonthlyBreakdownTable';
import { AccumulationTimelineSection } from './accumulationTimeline/AccumulationTimelineSection';
import { PolicyCostSection } from './policyCost/PolicyCostSection';
import { IdentityChecksPanel } from './IdentityChecksPanel';
import { ProjectionDataWarnings } from './ProjectionDataWarnings';
import { TranscriptDialog } from './TranscriptDialog';
import { EmptyState } from './EmptyState';

interface ProjectionDetailsContentProps {
  fundingSourceId: string | undefined;
  showEngineTools?: boolean;
}

/**
 * The body of the projection-details view: the wizard tools row plus the
 * policy/returns/monthly/identity cards. Shared by `ProjectionDetailsPage`
 * (full page, with a policy picker) and the funding-table slide-out.
 */
export function ProjectionDetailsContent({
  fundingSourceId,
  showEngineTools = true,
}: ProjectionDetailsContentProps) {
  const projection = useProjectionForPolicy(fundingSourceId);
  const policy = usePolicyById(fundingSourceId);
  const isWizard = useIsWizardSession();
  const [transcriptOpen, setTranscriptOpen] = useState(false);

  // Withheld here rather than at each call site, so the guarantee holds for BOTH
  // the funding-table slide-out (which also checks `useHasProjectionView`) and
  // the direct /projection-details/:policyId route, which renders this component
  // straight from the policy picker.
  //
  // These products have no strategy today, so they would fall through to the
  // "No projection data yet" empty state below — which reads as a transient
  // condition that a recalculation would fix. For them it is permanent. And if
  // one ever gains a strategy, a month-by-month breakdown would start rendering
  // for a number we already know is materially wrong.
  if (policy?.policyType && hasKnownIncorrectModeling(policy.policyType)) {
    return (
      <EmptyState
        title="Projection details unavailable for this product"
        body="Our modeling for this product does not yet match how the carrier pays benefits, so we are not showing a month-by-month breakdown. The policy still contributes to the plan totals."
      />
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-4">
        {policy?.policyType &&
          fundingPolicyTypeDefs[policy.policyType]?.hasUnverifiedModeling && (
            <UnverifiedModelingDisclaimer policyType={policy.policyType} />
          )}

        {projection && <ProjectionDataWarnings terms={projection.terms} />}

        {projection && (
          <div className="pointer-events-none sticky top-3 z-30 flex flex-wrap justify-end gap-2 [&_button:hover]:bg-gray-50 [&_button]:pointer-events-auto [&_button]:bg-white [&_button]:shadow-md">
            <PolicyDetailsPopover policy={policy} />
            <CarePlanDetailsPopover />
            {showEngineTools && isWizard && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setTranscriptOpen(true)}
              >
                View as text
              </Button>
            )}
          </div>
        )}

        {projection && (
          <TranscriptDialog
            projection={projection}
            open={transcriptOpen}
            onClose={() => setTranscriptOpen(false)}
          />
        )}

        {!projection ? (
          <EmptyState
            title="No projection data yet"
            body="Run a calculation on this client to populate the projection details."
          />
        ) : (
          <div className="space-y-3">
            <AccumulationTimelineSection
              projection={projection}
              policyType={policy?.policyType}
            />
            <MonthlyBreakdownTable
              projection={projection}
              policyType={policy?.policyType}
            />
            <ReturnsSummaryCard projection={projection} />
            <PolicyCostSection policy={policy} terms={projection.terms} />
            <IdentityChecksPanel projection={projection} />
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}
